import { SaveStatus } from 'lib-shared-legacy-types';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { selectQueueState } from 'app/utils/reselect';
import { useScheduleFetch } from 'components/powerFlowShared/hooks/useScheduleFetch';
import { useTagFetch } from 'components/powerFlowShared/hooks/useTagFetch';
import { FlowEditorChrome, FlowType } from 'fable/flowEditor';
import {
  withFlows,
  WithFlowsInjectedPropsT,
  withTags,
  WithTagsInjectedProps,
} from 'hocs';
import { FlowT } from 'hocs/flows/api/types';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { ElevioIntegrationProvider } from './elevioIntegrationProvider';
import styles from './flow.module.scss';
import { FlowMessengerAction } from './flow.types';
import { getNodesBasedOnFeatureFlags } from './getNodesBasedOnFeatureFlags';
import { useFlowData, useQueryElevioIntegration } from './hooks';

type FlowProps = WithFlowsInjectedPropsT<FlowMessengerAction> &
  WithTagsInjectedProps;

type RouterParams = {
  flowId: string;
};

const FlowImpl = (props: FlowProps): JSX.Element => {
  const { tags, flows } = props;
  const { item: flow, getItem: getFlowItem } = flows;
  const notify = useNotifications();
  const { flowId } = useParams<RouterParams>();
  const { queues } = useAppSelector(selectQueueState);
  const [saveStatus, setSaveStatus] = useState(SaveStatus.Saved);
  const [invalidNodes, setInvalidNodes] = useState<null | string[]>(null);

  const { actions, active, messengerId, setActions, setActive } = useFlowData(
    getFlowItem,
    flowId,
  );

  const elevioIntegration = useQueryElevioIntegration(messengerId);

  useTagFetch(tags, actions);
  const schedules = useScheduleFetch(actions);

  const featureFlags = useFeatureFlag([
    'integrations-for-dixa-messenger',
    'conversation-assistant-for-dixa-messenger',
    'chatbots-integration-settings',
    'has-custom-forms-feature',
  ]);
  const nodeTypes = getNodesBasedOnFeatureFlags(featureFlags);

  const hasUnsavedChanges = saveStatus <= SaveStatus.Unsaved;

  const handleOnChange = useCallback(
    ({
      actions: newActions,
      active: newActive,
    }: {
      name: string;
      actions: FlowMessengerAction[];
      active: boolean;
    }) => {
      if (
        !hasUnsavedChanges ||
        actions !== newActions ||
        active !== newActive
      ) {
        setSaveStatus(SaveStatus.Unsaved);
        setInvalidNodes(null);
        setActions(newActions);
        setActive(newActive);
      }
    },
    [actions, active, hasUnsavedChanges, setActive, setActions],
  );

  const getCurrentFlow = useCallback(
    (active: boolean): FlowT<FlowMessengerAction> | false => {
      if (flow !== null && actions !== null) {
        return {
          ...flow,
          actions,
          active: flow.active || active,
        };
      }

      return false;
    },
    [actions, flow],
  );

  const handleOnActivate = useCallback(async () => {
    const updatedFlow = getCurrentFlow(true);
    if (updatedFlow && flowId) {
      await flows.update(flowId, updatedFlow);
      setSaveStatus(SaveStatus.Saved);
      setActive(true);
    }
  }, [flowId, flows, getCurrentFlow, setActive]);

  const handleOnSave = useCallback(async () => {
    const updatedFlow = getCurrentFlow(false);
    if (updatedFlow && flowId) {
      try {
        await flows.update(flowId, updatedFlow);
        setSaveStatus(SaveStatus.Saved);
        notify(
          {
            title: 'Saved',
            text: `"${flow?.name}" is saved.`,
            time: 3000,
          },
          NotificationType.Success,
        );
      } catch (err: unknown) {
        setSaveStatus(SaveStatus.Failed);
        notify(
          {
            title: 'Could not save',
            text: `There was an issue when "${flow?.name}" was saved. ${err}`,
          },
          NotificationType.Error,
        );
      }
    }
  }, [flowId, flows, flow, getCurrentFlow, notify]);

  return (
    <ElevioIntegrationProvider elevioIntegration={elevioIntegration}>
      <div className={styles.root}>
        {flow !== null && (
          <FlowEditorChrome
            disableTitleEdit
            errorMessage={null}
            extraData={{
              name: flow.name,
              queues,
              tags: tags.items,
              schedules,
            }}
            flowType={FlowType.FLOW_TYPE_MESSENGER}
            goBackPath={'/settings/dixa-messenger'}
            hasUnsavedChanges={hasUnsavedChanges}
            initialData={flow}
            invalidNodes={invalidNodes}
            nodeTypes={nodeTypes}
            onActivate={handleOnActivate}
            onChange={handleOnChange}
            onSave={handleOnSave}
          />
        )}
      </div>
    </ElevioIntegrationProvider>
  );
};

export const Flow = withTags(
  withFlows<FlowMessengerAction, FlowProps>(FlowImpl),
);
